package org.catharsis.spendtel.core.impl

import androidx.compose.runtime.mutableStateOf
import dev.gitlive.firebase.auth.FirebaseAuth
import dev.gitlive.firebase.auth.FirebaseUser
import dev.gitlive.firebase.auth.GoogleAuthProvider
import dev.gitlive.firebase.firestore.FirebaseFirestore
import io.github.aakira.napier.Napier
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.map
import org.catharsis.spendtel.core.api.AnalyticsApi
import org.catharsis.spendtel.core.api.AuthenticationRepo
import org.catharsis.spendtel.core.api.ExpenseReportRepo
import org.catharsis.spendtel.core.api.StatementStorageRepo
import org.catharsis.spendtel.core.platform.PendingReportStore
import org.catharsis.spendtel.feature.data.EventName
import org.catharsis.spendtel.feature.data.SpendtelUser
import org.catharsis.spendtel.core.auth.authErrorMessage
import org.catharsis.spendtel.feature.data.toSpendtelUser

class AuthenticationRepoImpl(
    private val firebaseAuth: FirebaseAuth,
    private val firebaseFirestore: FirebaseFirestore,
    private val expenseReportRepo: ExpenseReportRepo,
    private val statementStorageRepo: StatementStorageRepo,
    private val analytics: AnalyticsApi,
): AuthenticationRepo {
    override val isAuthenticated: Boolean
        get() {
            return firebaseAuth.currentUser != null
        }

    override fun authSessionChanges(): Flow<Unit> =
        firebaseAuth.authStateChanged.map { }

    override suspend fun clearSession() {
        firebaseAuth.signOut()
    }

    override suspend fun getCurrentUser(): SpendtelUser? {
        val firebaseUser = firebaseAuth.currentUser ?: return null
        val uid = firebaseUser.uid
        print("AuthenticationRepoImpl getCurrentUser uid -> $uid")

        return try {
            val userSnapshot = firebaseFirestore.collection("USERS").document(uid).get()
            if (userSnapshot.exists) {
                userSnapshot.data<SpendtelUser>()
            } else {
                createAndPersistUserProfile(firebaseUser)
            }
        } catch (e: Exception) {
            Napier.e("Error getting current User -> $e")
            print("‼️ Error getting user $e")
            if (isPermissionDenied(e)) {
                firebaseUser.toSpendtelUser()
            } else {
                null
            }
        }
    }

    private suspend fun createAndPersistUserProfile(firebaseUser: FirebaseUser): SpendtelUser {
        val spendtelUser = newUserProfile(firebaseUser)
        if (!persistUserProfile(firebaseUser.uid, spendtelUser)) {
            Napier.w("Returning in-memory profile for ${firebaseUser.uid}; Firestore doc was not created")
        }
        return spendtelUser
    }

    private fun newUserProfile(firebaseUser: FirebaseUser): SpendtelUser {
        return firebaseUser.toSpendtelUser().copy(
            profileImage = placeholderProfileImages.random(),
            agreedToEULA = true,
        )
    }

    private suspend fun persistUserProfile(uid: String, spendtelUser: SpendtelUser): Boolean {
        return persistSpendtelUserProfile(firebaseFirestore, firebaseAuth, spendtelUser).isSuccess
    }

    private fun logAccountCreatedSafely() {
        try {
            analytics.logClickEvent(EventName.CREATED_ACCOUNT)
        } catch (e: Exception) {
            Napier.w("Analytics event failed for CREATED_ACCOUNT: $e")
        }
    }

    private fun isPermissionDenied(error: Throwable): Boolean {
        val message = error.message.orEmpty()
        return message.contains("PERMISSION_DENIED", ignoreCase = true) ||
            message.contains("Missing or insufficient permissions", ignoreCase = true)
    }

    override suspend fun signIn(email: String, password: String): Result<SpendtelUser> {
        try {
            val user = firebaseAuth.signInWithEmailAndPassword(email, password).user
            return if (user != null) {
                val snapshot = firebaseFirestore.collection("USERS").document(user.uid).get()
                val tvTallyUser = if (snapshot.exists) {
                    snapshot.data<SpendtelUser>()
                } else {
                    createAndPersistUserProfile(user)
                }
                analytics.logClickEvent(EventName.LOGGED_IN)
                Result.success(tvTallyUser)
            } else Result.failure(Throwable("User not found"))
        } catch (e: Exception) {
            Napier.e("signIn failed: $e")
            return Result.failure(Throwable(authErrorMessage(e)))
        }
    }

    override suspend fun signOut(): Result<Unit> {
        try {
            firebaseAuth.signOut()
            analytics.logClickEvent(EventName.SIGNED_OUT)
            return Result.success(Unit)
        } catch (e: Exception) {
            return Result.failure(e)
        }
    }

    override suspend fun createAccount(email: String, password: String): Result<SpendtelUser> {
        return try {
            val user = firebaseAuth.createUserWithEmailAndPassword(email, password).user
                ?: return Result.failure(Throwable("Unable to create an account, please try again"))

            val spendtelUser = newUserProfile(user)
            if (!persistUserProfile(user.uid, spendtelUser)) {
                Napier.w("Account auth created for ${user.uid} but Firestore profile write failed")
            }
            logAccountCreatedSafely()
            Result.success(spendtelUser)
        } catch (e: Exception) {
            Napier.e("createAccount failed: $e")
            Result.failure(Throwable(authErrorMessage(e)))
        }
    }

    override suspend fun forgotPassword(emailAddress: String): Result<Unit> {
        firebaseAuth.sendPasswordResetEmail(emailAddress)
        analytics.logClickEvent(EventName.FORGOT_PASSWORD)
        return Result.success(Unit)
    }

    override suspend fun deleteAccount(): Result<Unit> {
        val user = firebaseAuth.currentUser
            ?: return Result.failure(IllegalStateException("You must be signed in to delete your account."))
        val uid = user.uid

        return try {
            expenseReportRepo.deleteAllExpenseReports(uid).getOrThrow()
            statementStorageRepo.deleteAllUserStatements(uid).getOrThrow()
            firebaseFirestore.collection("USERS").document(uid).delete()
            PendingReportStore.clear()
            user.delete()
            analytics.logClickEvent(EventName.DELETED_ACCOUNT)
            Result.success(Unit)
        } catch (e: Exception) {
            Napier.e("deleteAccount failed for $uid: $e")
            val message = when {
                e.message?.contains("requires-recent-login", ignoreCase = true) == true ->
                    "For security, sign out and sign in again, then retry deleting your account."
                else -> e.message ?: "Could not delete your account. Please try again."
            }
            Result.failure(Throwable(message))
        }
    }

    override suspend fun signInWithGoogle(idToken: String, accessToken: String?): Result<SpendtelUser> {
        return try {
            val existingUser = firebaseAuth.currentUser
            val authResult = if (existingUser != null) {
                // Web signInWithPopup completes Firebase auth before this call.
                null to existingUser
            } else {
                val credential = GoogleAuthProvider.credential(idToken, accessToken)
                val result = firebaseAuth.signInWithCredential(credential)
                result to result.user
            }

            val firebaseUser = authResult.second
                ?: return Result.failure(Throwable("Google sign-in failed. Please try again."))

            val spendtelUser = try {
                val userDocument = firebaseFirestore.collection("USERS").document(firebaseUser.uid)
                val existingProfile = try {
                    val snapshot = userDocument.get()
                    if (snapshot.exists) snapshot.data<SpendtelUser>() else null
                } catch (_: Exception) {
                    null
                }

                existingProfile ?: createAndPersistUserProfile(firebaseUser)
            } catch (e: Exception) {
                if (isPermissionDenied(e)) {
                    Napier.w("Firestore blocked profile sync for ${firebaseUser.uid}; using auth profile")
                    return Result.success(
                        firebaseUser.toSpendtelUser().copy(
                            profileImage = placeholderProfileImages.random(),
                        )
                    )
                }
                throw e
            }

            val isNewUser = authResult.first?.additionalUserInfo?.isNewUser == true
            if (isNewUser) {
                analytics.logClickEvent(EventName.CREATED_ACCOUNT)
            } else {
                analytics.logClickEvent(EventName.LOGGED_IN)
            }
            Result.success(spendtelUser)
        } catch (e: Exception) {
            Result.failure(Throwable(e.message ?: "Google sign-in failed. Please try again."))
        }
    }

    companion object {
        val placeholderProfileImages = listOf(
            "https://cdn-icons-png.flaticon.com/512/6997/6997668.png",
            "https://cdn-icons-png.flaticon.com/512/6833/6833591.png",
            "https://cdn-icons-png.flaticon.com/512/6997/6997662.png",
            "https://cdn-icons-png.flaticon.com/512/6833/6833605.png",
            "https://cdn-icons-png.flaticon.com/512/3135/3135789.png",
            "https://cdn-icons-png.flaticon.com/512/6833/6833601.png",
            "https://cdn-icons-png.flaticon.com/512/780/780259.png",
            "https://cdn-icons-png.flaticon.com/512/1317/1317331.png",
            "https://cdn-icons-png.flaticon.com/512/8955/8955147.png",
        )
    }
}